/**
 * Data Access Layer - HTTP Client for Duty Log API
 *
 * Handles low-level HTTP communication with the backend API.
 * This layer is responsible for:
 * - Making HTTP requests
 * - Handling authentication
 * - Managing API endpoints
 * - HTTP error handling
 */

import { DutyLogEntry, DutyLogCreateRequest, DutyLogUpdateRequest } from '../../domain/model/dutylog';
import { getBackendUrl } from '../configService';
import { todayInNwt } from '../../utils/nwtDate';

export interface DutyContextResponse {
  userName: string;
  logContext: 'RDO' | 'TDO' | 'ADMIN';
  allowedRegions: string[];
}

export interface DutyLogApiClient {
  createEntry(entry: DutyLogCreateRequest): Promise<DutyLogEntry>;
  updateEntry(entry: DutyLogUpdateRequest): Promise<DutyLogEntry>;
  deleteEntry(id: string): Promise<void>;
  getDutyContext(): Promise<DutyContextResponse>;
}

export class HttpDutyLogApiClient implements DutyLogApiClient {
  private getBaseUrl(): string {
    const backendUrl = getBackendUrl();
    if (!backendUrl) {
      throw new Error('❌ FATAL: BACKEND_URL not configured in runtime config');
    }
    return `${backendUrl}/api/dutylogs`;
  }

  // Returns a real bearer token if one's in localStorage, otherwise null.
  // When null, callers omit the Authorization header and rely on the
  // `app.at` cookie carried by `credentials: 'include'` — that cookie is
  // the primary auth path. A 401 from the backend will surface as a
  // caught error in the modal, not a hard redirect from this client.
  private getAuthToken(): string | null {
    return localStorage.getItem('access_token') || null;
  }

  private buildHeaders(extra: Record<string, string> = {}): HeadersInit {
    const token = this.getAuthToken();
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...extra,
    };
  }

  async getDutyContext(): Promise<DutyContextResponse> {
    const response = await fetch(`${this.getBaseUrl()}/me/duty-context`, {
      method: 'GET',
      credentials: 'include',
      headers: this.buildHeaders(),
    });
    if (!response.ok) {
      throw new Error(`Failed to load duty context (HTTP ${response.status})`);
    }
    return await response.json();
  }

  async createEntry(entry: DutyLogCreateRequest): Promise<DutyLogEntry> {
    try {
      console.log('🔥 HttpDutyLogApiClient: Creating REAL duty log entry');

      const response = await fetch(this.getBaseUrl(), {
        method: 'PUT',
        credentials: 'include',
        headers: this.buildHeaders(),
        body: JSON.stringify({
          body: entry.body,
          logClass: entry.logClass,
          logUser: entry.logUser,
          logContext: entry.logContext,
          enhanced_body: entry.enhanced_body,
          enhancement_metadata: entry.enhancement_metadata
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const createdEntry = await response.json();
      return createdEntry;
    } catch (error) {
      console.error('HttpDutyLogApiClient error:', error);
      throw error;
    }
  }

  async updateEntry(entry: DutyLogUpdateRequest): Promise<DutyLogEntry> {
    try {
      const response = await fetch(`${this.getBaseUrl()}/${entry.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: this.buildHeaders(),
        body: JSON.stringify({
          body: entry.body
          // NOTE: Only send body field to preserve original logDate, logClass, logUser, logContext
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Handle successful PATCH response - external service may return 200 with no body
      let updatedEntry;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        // Response has JSON content
        updatedEntry = await response.json();
      } else {
        // Response has no JSON body (just 200 status) - construct response from original entry
        console.log('✅ Update successful - no JSON response body, constructing result from request');
        updatedEntry = {
          ...entry, // Include the id and updated body from request
          // Note: We don't have the updated logDate or other fields from the database
          // but the update was successful. The UI will refresh to get latest data.
        };
      }

      return updatedEntry;
    } catch (error) {
      console.error('HttpDutyLogApiClient update error:', error);
      throw error;
    }
  }

  async deleteEntry(id: string): Promise<void> {
    try {
      const response = await fetch(`${this.getBaseUrl()}/${id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: this.buildHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('HttpDutyLogApiClient delete error:', error);
      throw error;
    }
  }
}

// Mock implementation for testing
export class MockDutyLogApiClient implements DutyLogApiClient {
  private mockEntries: DutyLogEntry[] = [];

  async getDutyContext(): Promise<DutyContextResponse> {
    return {
      userName: 'Mock User',
      logContext: 'ADMIN',
      allowedRegions: ['HQ', 'WBNP', 'Inuvik', 'South Slave', 'Sahtu', 'Deh Cho', 'North Slave'],
    };
  }

  async createEntry(entry: DutyLogCreateRequest): Promise<DutyLogEntry> {
    const mockEntry: DutyLogEntry = {
      _id: `mock-${Date.now()}`,
      body: entry.body,
      logSummary: `${entry.logUser} ${todayInNwt()} ${new Date().toLocaleTimeString()}`,
      logDate: new Date().toISOString(),
      logClass: entry.logClass as any,
      logUser: entry.logUser,
      logContext: entry.logContext as any,
      enhanced_body: entry.enhanced_body,
      enhancement_metadata: entry.enhancement_metadata
    };

    this.mockEntries.unshift(mockEntry);
    console.log('🎭 MockDutyLogApiClient: Created mock entry:', mockEntry._id);
    return mockEntry;
  }

  async updateEntry(entry: DutyLogUpdateRequest): Promise<DutyLogEntry> {
    const index = this.mockEntries.findIndex(e => e._id === entry.id.toString());
    if (index === -1) {
      throw new Error(`Mock entry with ID ${entry.id} not found`);
    }

    const updatedEntry: DutyLogEntry = {
      ...this.mockEntries[index],
      body: entry.body,
      logUser: entry.logUser,
      logClass: entry.logClass as any,
      logContext: entry.logContext as any,
      logSummary: `${entry.logUser} ${todayInNwt()} ${new Date().toLocaleTimeString()}`
    };

    this.mockEntries[index] = updatedEntry;
    console.log('🎭 MockDutyLogApiClient: Updated mock entry:', updatedEntry._id);
    return updatedEntry;
  }

  async deleteEntry(id: string): Promise<void> {
    const index = this.mockEntries.findIndex(e => e._id === id);
    if (index === -1) {
      throw new Error(`Mock entry with ID ${id} not found`);
    }

    const deletedEntry = this.mockEntries.splice(index, 1)[0];
    console.log('🎭 MockDutyLogApiClient: Deleted mock entry:', deletedEntry._id);
  }
}
